import { c as _c } from "react/compiler-runtime";
import { storefrontIdeaPath, storefrontProductPath } from "@osmix/ui-primitives/idea-editor";
import { ArrowDown, ArrowDownWideNarrow, ArrowRight, ArrowUp, ArrowUpNarrowWide, CircleCheck, ExternalLink, ImageIcon, Link2, MapPin, Pencil, Plus, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { api, type Idea, type IdeaStatus } from "@/api";
import { ideaKeys, useIdeaCounts, useIdeaPage, useIdeas, useIdeaShops } from "@/api/hooks/use-ideas";
import { DataTablePagination } from "@/components/data-table";
import { serverDataTable, useUrlTable } from "@/lib/use-url-table";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { IdeaDetailDialog } from "@/components/idea-detail-dialog";
import { Button } from "@osmix/ui/button";
import { Select } from "@/components/ui/select";
import { storefrontBase } from "@/lib/storefront";
import { cn, formatPrice } from "@/lib/utils";

// «Идеи» консоли. Список и редактор повторяют раздел «Товары › Идеи» кабинета
// продавца (макеты 25.09.2026) — те же колонки, фильтры, метрики и шаги, в
// теме консоли. Сверху консоль добавляет своё: автор (магазин или OSMIX),
// очередь модерации и действия «опубликовать / отклонить / заблокировать» с
// примечанием модератора; вторая вкладка — подборка ленты вдохновения.

type LoadState = "idle" | "loading" | "ready" | "error";
type Tab = "list" | "curation";
type ListFilter = "all" | "moderation" | "unpublished" | "blocked";
type Moderation = {
  kind: "approve" | "reject" | "block";
  idea: Idea;
};
const FILTERS: ListFilter[] = ["all", "moderation", "unpublished", "blocked"];
const STATUS_TONE: Record<IdeaStatus, string> = {
  published: "bg-emerald-500 text-emerald-700",
  moderation: "bg-amber-500 text-amber-700",
  blocked: "bg-red-500 text-red-700",
  rejected: "bg-red-500 text-red-700",
  draft: "bg-muted-foreground text-muted-foreground",
  archived: "bg-muted-foreground text-muted-foreground"
};
const formatDate = (iso: string) => {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
};

// Адрес витрины: переменная сборки, соседний с API хост или web.osmix.ru -
// см. lib/storefront.ts (консоль на проде не на admin.*, а на admin-3jga8.*).
const storefront = storefrontBase;

// Cover thumbnail with a pin count (curation tab).
function IdeaCover(t0) {
  const $ = _c(13);
  const {
    idea,
    className
  } = t0;
  let t1;
  if ($[0] !== idea.images) {
    t1 = idea.images.reduce(_temp, 0);
    $[0] = idea.images;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const pins = t1;
  const cover = idea.images[0]?.url;
  let t2;
  if ($[2] !== className) {
    t2 = cn("relative shrink-0 overflow-hidden rounded bg-muted", className);
    $[2] = className;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== cover) {
    t3 = cover ? <img src={cover} alt="" className="size-full object-cover" /> : null;
    $[4] = cover;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <MapPin className="size-2.5" />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== pins) {
    t5 = <span className="absolute bottom-0.5 right-0.5 flex items-center gap-0.5 rounded bg-black/60 px-1 text-[10px] text-white">{t4}{pins}</span>;
    $[7] = pins;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t5) {
    t6 = <div className={t2}>{t3}{t5}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}
function _temp(n, img) {
  return n + img.pins.length;
}
export function IdeasPage() {
  const $ = _c(21);
  const {
    t
  } = useTranslation();
  const [tab, setTab] = useState("list");
  let t0;
  if ($[0] !== t) {
    t0 = t("ideas.heading");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <h1 className="text-xl font-bold">{t0}</h1>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t) {
    t2 = t("ideas.description");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <p className="text-sm text-muted-foreground">{t2}</p>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t3) {
    t4 = <div data-tour="ideas-intro">{t1}{t3}</div>;
    $[8] = t1;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = ["list", "curation"];
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t || $[13] !== tab) {
    t6 = <div className="flex gap-4 border-b" data-tour="ideas-tabs">{(t5 as Tab[]).map(value => <button key={value} type="button" onClick={() => setTab(value)} className={cn("-mb-px border-b-2 px-1 pb-2 text-sm font-medium transition-colors", tab === value ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>{t(`ideas.tabs.${value}`)}</button>)}</div>;
    $[12] = t;
    $[13] = tab;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== tab) {
    t7 = tab === "list" ? <ListTab /> : <CurationTab />;
    $[15] = tab;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t4 || $[18] !== t6 || $[19] !== t7) {
    t8 = <div className="space-y-6">{t4}{t6}{t7}</div>;
    $[17] = t4;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
function ListTab() {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Срез, магазин, порядок и страница — на сервере, в адресной строке.
  const url = useUrlTable<"date">({
    filters: ["list", "shopId"],
    defaultSort: {
      key: "date",
      dir: "desc"
    },
    defaultPageSize: 25,
    sortKeys: ["date"]
  });
  const filter: ListFilter = (FILTERS as string[]).includes(url.filters.list ?? "") ? url.filters.list as ListFilter : "all";
  const shop = url.filters.shopId ?? "";
  const sortAsc = url.sort.dir === "asc";
  const {
    data,
    error: loadError,
    isFetching
  } = useIdeaPage({
    list: filter,
    shopId: shop || undefined,
    dir: url.sort.dir,
    page: url.page,
    pageSize: url.pageSize
  });
  const countsQuery = useIdeaCounts(shop);
  const shopsQuery = useIdeaShops();
  const [pending, setPending] = useState<Moderation | null>(null);
  const [note, setNote] = useState("");
  const [pendingDelete, setPendingDelete] = useState<Idea | null>(null);
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    const saved = params.get("saved");
    if (!saved) return;
    setNotice(t(saved === "created" ? "ideas.list.created" : "ideas.list.updated"));
    navigate("/ideas", {
      replace: true
    });
    const timer = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(timer);
  }, [params, navigate, t]);
  const shops = (shopsQuery.data ?? []).map(s => [s.id, s.name] as const);
  const count = (f: ListFilter) => countsQuery.data?.[f] ?? 0;
  const rows = data?.items ?? [];
  const view = serverDataTable(url, rows, data?.total ?? 0);
  const load: LoadState = isFetching && !data ? "loading" : loadError ? "error" : data ? "ready" : "idle";
  const moderate = async () => {
    if (!pending) return;
    setActing(true);
    try {
      const id = pending.idea.id;
      if (pending.kind === "approve") await api.ideas.approve(id, note);else if (pending.kind === "reject") await api.ideas.reject(id, note);else await api.ideas.block(id, note);
      setPending(null);
      setActionError(null);
      await queryClient.invalidateQueries({
        queryKey: ideaKeys.all
      });
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setActing(false);
    }
  };
  const remove = async () => {
    if (!pendingDelete) return;
    setActing(true);
    try {
      await api.ideas.remove([pendingDelete.id]);
      setPendingDelete(null);
      await queryClient.invalidateQueries({
        queryKey: ideaKeys.all
      });
    } catch (e_0) {
      setActionError((e_0 as Error).message);
    } finally {
      setActing(false);
    }
  };
  const copyLink = (idea: Idea) => void navigator.clipboard?.writeText(`${storefront()}${storefrontIdeaPath(idea.id)}`).then(() => setNotice(t("ideas.list.linkCopied"))).catch(() => {});
  const error = actionError ?? (loadError ? loadError.message : null);
  return <div className="space-y-4">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<p className="max-w-3xl text-sm">{t("ideas.list.subtitle")}</p>
				{notice && <div role="status" className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
						<CircleCheck className="size-4" />
						{notice}
					</div>}
			</div>
			<Button asChild data-tour="ideas-create">
				<Link to="/ideas/new">
					<Plus className="size-4" />
					{t("ideas.list.create")}
				</Link>
			</Button>

			<div className="flex flex-wrap items-center justify-between gap-3" data-tour="ideas-filters">
				<div className="flex flex-wrap gap-3">
					{FILTERS.map(f_0 => <button key={f_0} type="button" onClick={() => url.setFilter("list", f_0 === "all" ? "" : f_0)} className={cn("text-sm font-medium", filter === f_0 ? "text-primary" : "text-foreground/80 hover:text-foreground")}>
							{f_0 === "all" ? t("ideas.list.filters.all") : `${t(`ideas.list.filters.${f_0}`)} (${count(f_0)})`}
						</button>)}
				</div>
				<Select value={shop} onChange={e_1 => url.setFilter("shopId", e_1.target.value)} aria-label={t("ideas.list.shop")}>
					<option value="">{t("ideas.list.allShops")}</option>
					{shops.map(([id_0, name]) => <option key={id_0} value={id_0}>
							{name}
						</option>)}
				</Select>
			</div>

			{error && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
					{error}
				</div>}
			{load === "loading" && <p className="text-sm text-muted-foreground">{t("ideas.loading")}</p>}
			{load === "ready" && rows.length === 0 && <p className="text-sm text-muted-foreground">{t("ideas.empty")}</p>}

			{rows.length > 0 && <div className="overflow-x-auto border-t" data-tour="ideas-table">
					<table className="w-full min-w-[1560px] border-separate border-spacing-y-3 text-left text-sm">
						<thead className="text-muted-foreground">
							<tr>
								<th className="w-[42px]" />
								<th className="w-[198px] py-2 pr-3 font-normal">{t("ideas.list.col.photo")}</th>
								<th className="w-[150px] py-2 pr-3 font-normal">{t("ideas.list.col.title")}</th>
								<th className="w-[150px] py-2 pr-3 font-normal">{t("ideas.list.col.description")}</th>
								<th className="w-[130px] py-2 pr-3 font-normal">{t("ideas.list.col.author")}</th>
								<th className="w-[250px] py-2 pr-3 font-normal">{t("ideas.list.col.products")}</th>
								<th className="w-[130px] py-2 pr-3 font-normal">{t("ideas.list.col.total")}</th>
								<th className="w-[190px] py-2 pr-3 font-normal" data-tour="ideas-status">
									{t("ideas.list.col.status")}
								</th>
								<th className="w-[80px] py-2 pr-3 font-normal" title={t("ideas.list.col.reachHelp")} data-tour="ideas-metrics">
									{t("ideas.list.col.reach")} ⓘ
								</th>
								<th className="w-[80px] py-2 pr-3 font-normal" title={t("ideas.list.col.impressionsHelp")}>
									{t("ideas.list.col.impressions")} ⓘ
								</th>
								<th className="w-[98px] py-2 pr-3 font-normal">{t("ideas.list.col.clicks")}</th>
								<th className="w-[90px] py-2 pr-3 font-normal">{t("ideas.list.col.favorites")}</th>
								<th className="w-[90px] py-2 pr-3 font-normal">{t("ideas.list.col.cart")}</th>
								<th className="w-[117px] py-2 pr-3 font-normal">
									<button type="button" onClick={() => url.toggleSort("date")} className="flex items-center gap-2">
										{t("ideas.list.col.date")}
										{sortAsc ? <ArrowUpNarrowWide className="size-4" /> : <ArrowDownWideNarrow className="size-4" />}
									</button>
								</th>
							</tr>
						</thead>
						<tbody>
							{rows.map(idea_0 => <Row key={idea_0.id} idea={idea_0} onCopyLink={() => copyLink(idea_0)} onDelete={() => setPendingDelete(idea_0)} onModerate={kind => {
            setNote(kind === "approve" ? "" : idea_0.moderatorNote ?? "");
            setActionError(null);
            setPending({
              kind,
              idea: idea_0
            });
          }} />)}
						</tbody>
					</table>
				</div>}
			{rows.length > 0 && <DataTablePagination table={view} />}

			<ConfirmDialog open={!!pending} busy={acting} title={pending ? `${t(`ideas.moderate.${pending.kind}Title`)} — ${pending.idea.title}` : ""} description={pending ? t(`ideas.moderate.${pending.kind}Body`) : undefined} confirmLabel={pending ? t(`ideas.moderate.${pending.kind}`) : undefined} confirmVariant={pending?.kind === "approve" ? "default" : "destructive"} onConfirm={() => void moderate()} onClose={() => setPending(null)}>
				<label className="flex flex-col gap-1.5 text-sm">
					{t("ideas.moderate.note")}
					<textarea value={note} onChange={e_2 => setNote(e_2.target.value)} placeholder={t("ideas.moderate.notePlaceholder")} className="h-24 resize-none rounded-md border border-input bg-transparent px-3 py-2 outline-none" />
				</label>
			</ConfirmDialog>

			<ConfirmDialog open={!!pendingDelete} busy={acting} title={pendingDelete?.title ?? ""} description={t("ideas.list.deleteConfirm")} confirmLabel={t("ideas.list.delete")} confirmVariant="destructive" onConfirm={() => void remove()} onClose={() => setPendingDelete(null)} />
		</div>;
}
function Row(t0) {
  const $ = _c(164);
  const {
    idea,
    onCopyLink,
    onDelete,
    onModerate
  } = t0;
  const {
    t
  } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  let t1;
  if ($[0] !== idea.products) {
    t1 = idea.products ?? [];
    $[0] = idea.products;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const products = t1;
  let t2;
  if ($[2] !== expanded || $[3] !== products) {
    t2 = expanded ? products : products.slice(0, 3);
    $[2] = expanded;
    $[3] = products;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const shown = t2;
  const cover = idea.images[0]?.url;
  const m = idea.metrics;
  const t3 = STATUS_TONE[idea.status];
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[5] !== cover || $[6] !== expanded || $[7] !== idea.description || $[8] !== idea.discountedTotal || $[9] !== idea.id || $[10] !== idea.productCount || $[11] !== idea.shopId || $[12] !== idea.shopName || $[13] !== idea.title || $[14] !== idea.totalPrice || $[15] !== onCopyLink || $[16] !== onDelete || $[17] !== products.length || $[18] !== shown || $[19] !== t || $[20] !== t3) {
    const tone = t3.split(" ");
    t8 = "align-top";
    const t16 = `/ideas/${idea.id}/edit`;
    let t17;
    if ($[33] !== t) {
      t17 = t("ideas.list.edit");
      $[33] = t;
      $[34] = t17;
    } else {
      t17 = $[34];
    }
    let t18;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <Pencil className="size-5" strokeWidth={1.5} />;
      $[35] = t18;
    } else {
      t18 = $[35];
    }
    let t19;
    if ($[36] !== t16 || $[37] !== t17) {
      t19 = <Link to={t16} aria-label={t17} className="flex size-6 items-center justify-center hover:text-primary">{t18}</Link>;
      $[36] = t16;
      $[37] = t17;
      $[38] = t19;
    } else {
      t19 = $[38];
    }
    let t20;
    if ($[39] !== t) {
      t20 = t("ideas.list.copyLink");
      $[39] = t;
      $[40] = t20;
    } else {
      t20 = $[40];
    }
    let t21;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <Link2 className="size-5" strokeWidth={1.5} />;
      $[41] = t21;
    } else {
      t21 = $[41];
    }
    let t22;
    if ($[42] !== onCopyLink || $[43] !== t20) {
      t22 = <button type="button" aria-label={t20} onClick={onCopyLink} className="flex size-6 items-center justify-center hover:text-primary">{t21}</button>;
      $[42] = onCopyLink;
      $[43] = t20;
      $[44] = t22;
    } else {
      t22 = $[44];
    }
    let t23;
    if ($[45] !== t) {
      t23 = t("ideas.list.delete");
      $[45] = t;
      $[46] = t23;
    } else {
      t23 = $[46];
    }
    let t24;
    if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = <Trash2 className="size-5" strokeWidth={1.5} />;
      $[47] = t24;
    } else {
      t24 = $[47];
    }
    let t25;
    if ($[48] !== onDelete || $[49] !== t23) {
      t25 = <button type="button" aria-label={t23} onClick={onDelete} className="flex size-6 items-center justify-center hover:text-primary">{t24}</button>;
      $[48] = onDelete;
      $[49] = t23;
      $[50] = t25;
    } else {
      t25 = $[50];
    }
    if ($[51] !== t19 || $[52] !== t22 || $[53] !== t25) {
      t9 = <td><div className="flex flex-col items-start gap-3">{t19}{t22}{t25}</div></td>;
      $[51] = t19;
      $[52] = t22;
      $[53] = t25;
      $[54] = t9;
    } else {
      t9 = $[54];
    }
    if ($[55] !== cover || $[56] !== idea.title) {
      t10 = <td className="pr-3">{cover ? <img src={cover} alt={idea.title} className="h-[120px] w-auto max-w-[186px] rounded object-cover" /> : <span className="flex h-[120px] w-[90px] items-center justify-center rounded bg-muted text-muted-foreground"><ImageIcon className="size-6" /></span>}</td>;
      $[55] = cover;
      $[56] = idea.title;
      $[57] = t10;
    } else {
      t10 = $[57];
    }
    if ($[58] !== idea.title) {
      t11 = <td className="pr-3"><p className="line-clamp-3">{idea.title}</p></td>;
      $[58] = idea.title;
      $[59] = t11;
    } else {
      t11 = $[59];
    }
    if ($[60] !== idea.description) {
      t12 = <td className="pr-3"><p className="line-clamp-3">{idea.description}</p></td>;
      $[60] = idea.description;
      $[61] = t12;
    } else {
      t12 = $[61];
    }
    const t26 = idea.shopName ?? idea.shopId;
    if ($[62] !== t26) {
      t13 = <td className="pr-3">{t26}</td>;
      $[62] = t26;
      $[63] = t13;
    } else {
      t13 = $[63];
    }
    let t27;
    if ($[64] !== idea.productCount || $[65] !== t) {
      t27 = t("ideas.list.products", {
        count: idea.productCount
      });
      $[64] = idea.productCount;
      $[65] = t;
      $[66] = t27;
    } else {
      t27 = $[66];
    }
    let t28;
    if ($[67] !== t27) {
      t28 = <span>{t27}</span>;
      $[67] = t27;
      $[68] = t28;
    } else {
      t28 = $[68];
    }
    let t29;
    if ($[69] !== shown || $[70] !== t) {
      let t30;
      if ($[72] !== t) {
        t30 = p => <a key={p.id} href={`${storefront()}${storefrontProductPath(p.id)}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:underline"><span className="max-w-[120px] truncate">{p.name}</span><span className="shrink-0 font-medium">{t("ideas.list.art", {
              article: p.article
            })}</span><ExternalLink className="size-4 shrink-0" /></a>;
        $[72] = t;
        $[73] = t30;
      } else {
        t30 = $[73];
      }
      t29 = shown.map(t30);
      $[69] = shown;
      $[70] = t;
      $[71] = t29;
    } else {
      t29 = $[71];
    }
    let t30;
    if ($[74] !== expanded || $[75] !== products.length || $[76] !== t) {
      t30 = products.length > 3 && !expanded && <button type="button" onClick={() => setExpanded(true)} className="flex w-fit items-center gap-2 font-medium">{t("ideas.list.allProducts")}<ArrowRight className="size-4" /></button>;
      $[74] = expanded;
      $[75] = products.length;
      $[76] = t;
      $[77] = t30;
    } else {
      t30 = $[77];
    }
    if ($[78] !== t28 || $[79] !== t29 || $[80] !== t30) {
      t14 = <td className="pr-3"><div className="flex flex-col gap-2">{t28}{t29}{t30}</div></td>;
      $[78] = t28;
      $[79] = t29;
      $[80] = t30;
      $[81] = t14;
    } else {
      t14 = $[81];
    }
    const t31 = idea.discountedTotal ?? idea.totalPrice ?? 0;
    let t32;
    if ($[82] !== t31) {
      t32 = formatPrice(t31);
      $[82] = t31;
      $[83] = t32;
    } else {
      t32 = $[83];
    }
    const t33 = `/ideas/${idea.id}/edit`;
    let t34;
    if ($[84] !== t) {
      t34 = t("ideas.list.editTotal");
      $[84] = t;
      $[85] = t34;
    } else {
      t34 = $[85];
    }
    let t35;
    if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
      t35 = <Pencil className="size-4" strokeWidth={1.5} />;
      $[86] = t35;
    } else {
      t35 = $[86];
    }
    let t36;
    if ($[87] !== t33 || $[88] !== t34) {
      t36 = <Link to={t33} aria-label={t34}>{t35}</Link>;
      $[87] = t33;
      $[88] = t34;
      $[89] = t36;
    } else {
      t36 = $[89];
    }
    let t37;
    if ($[90] !== t32 || $[91] !== t36) {
      t37 = <span className="flex items-center gap-2 font-medium">{t32}{t36}</span>;
      $[90] = t32;
      $[91] = t36;
      $[92] = t37;
    } else {
      t37 = $[92];
    }
    let t38;
    if ($[93] !== idea.discountedTotal || $[94] !== idea.totalPrice) {
      t38 = idea.totalPrice !== undefined && idea.discountedTotal !== undefined && idea.discountedTotal !== idea.totalPrice && <span className="text-xs text-muted-foreground line-through">{formatPrice(idea.totalPrice)}</span>;
      $[93] = idea.discountedTotal;
      $[94] = idea.totalPrice;
      $[95] = t38;
    } else {
      t38 = $[95];
    }
    if ($[96] !== t37 || $[97] !== t38) {
      t15 = <td className="whitespace-nowrap pr-3"><div className="flex flex-col gap-1">{t37}{t38}</div></td>;
      $[96] = t37;
      $[97] = t38;
      $[98] = t15;
    } else {
      t15 = $[98];
    }
    t7 = "pr-3";
    t6 = "flex flex-col gap-1.5";
    t5 = cn("flex items-center gap-1 text-xs font-medium", tone[1]);
    t4 = cn("size-2.5 rounded-full", tone[0]);
    $[5] = cover;
    $[6] = expanded;
    $[7] = idea.description;
    $[8] = idea.discountedTotal;
    $[9] = idea.id;
    $[10] = idea.productCount;
    $[11] = idea.shopId;
    $[12] = idea.shopName;
    $[13] = idea.title;
    $[14] = idea.totalPrice;
    $[15] = onCopyLink;
    $[16] = onDelete;
    $[17] = products.length;
    $[18] = shown;
    $[19] = t;
    $[20] = t3;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    t10 = $[21];
    t11 = $[22];
    t12 = $[23];
    t13 = $[24];
    t14 = $[25];
    t15 = $[26];
    t4 = $[27];
    t5 = $[28];
    t6 = $[29];
    t7 = $[30];
    t8 = $[31];
    t9 = $[32];
  }
  let t16;
  if ($[99] !== t4) {
    t16 = <span className={t4} />;
    $[99] = t4;
    $[100] = t16;
  } else {
    t16 = $[100];
  }
  const t17 = `ideas.status.${idea.status}`;
  let t18;
  if ($[101] !== t || $[102] !== t17) {
    t18 = t(t17);
    $[101] = t;
    $[102] = t17;
    $[103] = t18;
  } else {
    t18 = $[103];
  }
  let t19;
  if ($[104] !== t16 || $[105] !== t18 || $[106] !== t5) {
    t19 = <span className={t5}>{t16}{t18}</span>;
    $[104] = t16;
    $[105] = t18;
    $[106] = t5;
    $[107] = t19;
  } else {
    t19 = $[107];
  }
  let t20;
  if ($[108] !== idea.moderatorNote) {
    t20 = idea.moderatorNote && <p className="line-clamp-3">{idea.moderatorNote}</p>;
    $[108] = idea.moderatorNote;
    $[109] = t20;
  } else {
    t20 = $[109];
  }
  let t21;
  if ($[110] !== idea.status || $[111] !== onModerate || $[112] !== t) {
    t21 = idea.status !== "published" && <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => onModerate("approve")}>{t("ideas.moderate.approve")}</Button>;
    $[110] = idea.status;
    $[111] = onModerate;
    $[112] = t;
    $[113] = t21;
  } else {
    t21 = $[113];
  }
  let t22;
  if ($[114] !== idea.status || $[115] !== onModerate || $[116] !== t) {
    t22 = idea.status !== "rejected" && idea.status !== "draft" && <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => onModerate("reject")}>{t("ideas.moderate.reject")}</Button>;
    $[114] = idea.status;
    $[115] = onModerate;
    $[116] = t;
    $[117] = t22;
  } else {
    t22 = $[117];
  }
  let t23;
  if ($[118] !== idea.status || $[119] !== onModerate || $[120] !== t) {
    t23 = idea.status !== "blocked" && <Button size="sm" variant="outline" className="h-7 px-2 text-destructive" onClick={() => onModerate("block")}>{t("ideas.moderate.block")}</Button>;
    $[118] = idea.status;
    $[119] = onModerate;
    $[120] = t;
    $[121] = t23;
  } else {
    t23 = $[121];
  }
  let t24;
  if ($[122] !== t21 || $[123] !== t22 || $[124] !== t23) {
    t24 = <div className="flex flex-wrap gap-1">{t21}{t22}{t23}</div>;
    $[122] = t21;
    $[123] = t22;
    $[124] = t23;
    $[125] = t24;
  } else {
    t24 = $[125];
  }
  let t25;
  if ($[126] !== t19 || $[127] !== t20 || $[128] !== t24 || $[129] !== t6) {
    t25 = <div className={t6}>{t19}{t20}{t24}</div>;
    $[126] = t19;
    $[127] = t20;
    $[128] = t24;
    $[129] = t6;
    $[130] = t25;
  } else {
    t25 = $[130];
  }
  let t26;
  if ($[131] !== t25 || $[132] !== t7) {
    t26 = <td className={t7}>{t25}</td>;
    $[131] = t25;
    $[132] = t7;
    $[133] = t26;
  } else {
    t26 = $[133];
  }
  const t27 = m?.reach ?? 0;
  let t28;
  if ($[134] !== t27) {
    t28 = <td className="pr-3">{t27}</td>;
    $[134] = t27;
    $[135] = t28;
  } else {
    t28 = $[135];
  }
  const t29 = m?.impressions ?? 0;
  let t30;
  if ($[136] !== t29) {
    t30 = <td className="pr-3">{t29}</td>;
    $[136] = t29;
    $[137] = t30;
  } else {
    t30 = $[137];
  }
  const t31 = m?.productClicks ?? 0;
  let t32;
  if ($[138] !== t31) {
    t32 = <td className="pr-3">{t31}</td>;
    $[138] = t31;
    $[139] = t32;
  } else {
    t32 = $[139];
  }
  const t33 = m?.favorites ?? 0;
  let t34;
  if ($[140] !== t33) {
    t34 = <td className="pr-3">{t33}</td>;
    $[140] = t33;
    $[141] = t34;
  } else {
    t34 = $[141];
  }
  const t35 = m?.cartAdds ?? 0;
  let t36;
  if ($[142] !== t35) {
    t36 = <td className="pr-3">{t35}</td>;
    $[142] = t35;
    $[143] = t36;
  } else {
    t36 = $[143];
  }
  let t37;
  if ($[144] !== idea.createdAt) {
    t37 = formatDate(idea.createdAt);
    $[144] = idea.createdAt;
    $[145] = t37;
  } else {
    t37 = $[145];
  }
  let t38;
  if ($[146] !== t37) {
    t38 = <td className="pr-3">{t37}</td>;
    $[146] = t37;
    $[147] = t38;
  } else {
    t38 = $[147];
  }
  let t39;
  if ($[148] !== t10 || $[149] !== t11 || $[150] !== t12 || $[151] !== t13 || $[152] !== t14 || $[153] !== t15 || $[154] !== t26 || $[155] !== t28 || $[156] !== t30 || $[157] !== t32 || $[158] !== t34 || $[159] !== t36 || $[160] !== t38 || $[161] !== t8 || $[162] !== t9) {
    t39 = <tr className={t8}>{t9}{t10}{t11}{t12}{t13}{t14}{t15}{t26}{t28}{t30}{t32}{t34}{t36}{t38}</tr>;
    $[148] = t10;
    $[149] = t11;
    $[150] = t12;
    $[151] = t13;
    $[152] = t14;
    $[153] = t15;
    $[154] = t26;
    $[155] = t28;
    $[156] = t30;
    $[157] = t32;
    $[158] = t34;
    $[159] = t36;
    $[160] = t38;
    $[161] = t8;
    $[162] = t9;
    $[163] = t39;
  } else {
    t39 = $[163];
  }
  return t39;
}
function CurationTab() {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const {
    data,
    error: loadError,
    isFetching
  } = useIdeas({
    status: "published"
  });
  const published = data ?? [];
  // Ordered list of featured idea ids — the buyer feed reads top-down.
  const [order, setOrder] = useState<string[]>([]);
  const [seeded, setSeeded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [detail, setDetail] = useState<Idea | null>(null);

  // Seed the editable order once, from the first fetched list; background
  // refetches must not clobber an in-progress reordering.
  useEffect(() => {
    if (seeded || !data) return;
    setOrder(data.filter(i => i.feedRank !== null).sort((a, b) => (a.feedRank ?? 0) - (b.feedRank ?? 0)).map(i_0 => i_0.id));
    setSeeded(true);
  }, [seeded, data]);
  const load: LoadState = isFetching ? "loading" : loadError ? "error" : data ? "ready" : "idle";
  const error = actionError ?? (loadError ? loadError.message : null);
  const byId = (id: string) => published.find(i_1 => i_1.id === id);
  const featured = order.map(byId).filter((i_2): i_2 is Idea => !!i_2);
  const available = published.filter(i_3 => !order.includes(i_3.id));
  const move = (index: number, delta: number) => {
    const next = [...order];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    const current = next[index];
    const targetValue = next[target];
    if (!current || !targetValue) return;
    next[index] = targetValue;
    next[target] = current;
    setOrder(next);
    setSaved(false);
  };
  const add = (id_0: string) => {
    setOrder([...order, id_0]);
    setSaved(false);
  };
  const remove = (id_1: string) => {
    setOrder(order.filter(x => x !== id_1));
    setSaved(false);
  };
  const save = async () => {
    setSaving(true);
    setActionError(null);
    try {
      await api.ideas.curate(order);
      setSaved(true);
      await queryClient.invalidateQueries({
        queryKey: ideaKeys.all
      });
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  return <div className="space-y-4">
			<div className="flex items-center justify-between gap-2">
				<p className="text-sm text-muted-foreground">{t("ideas.curatedDescription")}</p>
				<div className="flex items-center gap-2">
					{saved && <span className="text-xs text-muted-foreground">{t("ideas.saved")}</span>}
					<Button size="sm" onClick={save} disabled={saving || load !== "ready"}>
						{saving ? t("ideas.saving") : t("ideas.save")}
					</Button>
				</div>
			</div>

			{error && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
					{error}
				</div>}
			{load === "loading" && <p className="text-sm text-muted-foreground">{t("ideas.loading")}</p>}

			<div className="grid gap-6 lg:grid-cols-2">
				<section className="space-y-2">
					<h2 className="text-sm font-semibold">{t("ideas.curatedTitle")}</h2>
					{featured.length === 0 ? <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
							{t("ideas.curatedEmpty")}
						</p> : <ol className="space-y-2">
							{featured.map((idea, index_0) => <li key={idea.id} className="flex items-center gap-3 rounded-lg border p-2">
									<span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
										{index_0 + 1}
									</span>
									<button type="button" onClick={() => setDetail(idea)} className="flex min-w-0 flex-1 items-center gap-3 rounded-md text-left transition-colors hover:bg-muted/50">
										<IdeaCover idea={idea} className="size-12" />
										<div className="min-w-0 flex-1">
											<p className="line-clamp-1 text-sm font-medium">{idea.title}</p>
											<p className="text-xs text-muted-foreground">
												{idea.shopName ?? idea.shopId}
											</p>
										</div>
									</button>
									<div className="flex items-center gap-1">
										<Button size="icon-sm" variant="ghost" onClick={() => move(index_0, -1)} disabled={index_0 === 0} aria-label={t("ideas.moveUp")}>
											<ArrowUp className="size-4" />
										</Button>
										<Button size="icon-sm" variant="ghost" onClick={() => move(index_0, 1)} disabled={index_0 === featured.length - 1} aria-label={t("ideas.moveDown")}>
											<ArrowDown className="size-4" />
										</Button>
										<Button size="icon-sm" variant="ghost" onClick={() => remove(idea.id)} aria-label={t("ideas.remove")}>
											<X className="size-4" />
										</Button>
									</div>
								</li>)}
						</ol>}
				</section>

				<section className="space-y-2">
					<h2 className="text-sm font-semibold">{t("ideas.availableTitle")}</h2>
					{available.length === 0 ? <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
							{t("ideas.availableEmpty")}
						</p> : <ul className="space-y-2">
							{available.map(idea_0 => <li key={idea_0.id} className="flex items-center gap-3 rounded-lg border p-2">
									<button type="button" onClick={() => setDetail(idea_0)} className="flex min-w-0 flex-1 items-center gap-3 rounded-md text-left transition-colors hover:bg-muted/50">
										<IdeaCover idea={idea_0} className="size-12" />
										<div className="min-w-0 flex-1">
											<p className="line-clamp-1 text-sm font-medium">{idea_0.title}</p>
											<p className="text-xs text-muted-foreground">
												{idea_0.shopName ?? idea_0.shopId}
											</p>
										</div>
									</button>
									<Button size="sm" variant="outline" onClick={() => add(idea_0.id)}>
										<Plus className="size-4" />
										{t("ideas.addToFeed")}
									</Button>
								</li>)}
						</ul>}
				</section>
			</div>

			<IdeaDetailDialog idea={detail} onClose={() => setDetail(null)} />
		</div>;
}